import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, FileText, Plus, CheckCircle, AlertCircle, Stethoscope } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';
import useWebSocket from './hooks/useWebSocket';
import DoctorRoutingModal from './DoctorRoutingModal';
import PatientPathwaySlip from './PatientPathwaySlip';

const DoctorDashboard = ({ onNavigate }) => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [todayAppointments, setTodayAppointments] = useState([]);
    const [myPatients, setMyPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [showRoutingModal, setShowRoutingModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [diagnosis, setDiagnosis] = useState('');
    const [showPathwaySlip, setShowPathwaySlip] = useState(false);
    const [pathwayData, setPathwayData] = useState(null);

    useEffect(() => {
        fetchDoctorData();
    }, [selectedDate]);

    // WebSocket for real-time appointment and patient updates
    useWebSocket({
        onMessage: (message) => {
            // Refresh data when appointments or patients are updated
            if (message.type === 'appointment_updated' || 
                message.type === 'appointment_created' ||
                message.type === 'patient_status_changed' ||
                message.type === 'queue_updated') {
                console.log('Doctor dashboard received update:', message.type);
                fetchDoctorData();
            }
        },
        onConnect: () => {
            console.log('Doctor dashboard connected to real-time updates');
        }
    });

    const fetchDoctorData = async () => {
        setLoading(true);
        try {
            const [appointments, patients] = await Promise.all([
                apiService.getMyAppointmentsToday(selectedDate),
                apiService.getMyPatients(10) // Get recent 10 patients
            ]);
            
            setTodayAppointments(appointments);
            setMyPatients(patients);
        } catch (error) {
            console.error('Error fetching doctor data:', error);
            showAlert('Failed to load dashboard data', 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (dateTimeString) => {
        return new Date(dateTimeString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'scheduled': return 'bg-blue-100 text-blue-800';
            case 'confirmed': return 'bg-green-100 text-green-800';
            case 'in-progress': return 'bg-yellow-100 text-yellow-800';
            case 'completed': return 'bg-gray-100 text-gray-800';
            case 'cancelled': return 'bg-red-100 text-red-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'text-red-600';
            case 'medium': return 'text-yellow-600';
            case 'low': return 'text-green-600';
            default: return 'text-gray-600';
        }
    };

    const handleUpdateAppointmentStatus = async (appointmentId, newStatus) => {
        try {
            await apiService.updateAppointment({
                id: appointmentId,
                status: newStatus
            });
            
            // Update local state
            setTodayAppointments(prev => 
                prev.map(apt => 
                    apt.id === appointmentId 
                        ? { ...apt, status: newStatus }
                        : apt
                )
            );
            
            showAlert(`Appointment status updated to ${newStatus}`, 'success');
        } catch (error) {
            console.error('Error updating appointment:', error);
            showAlert('Failed to update appointment status', 'error');
        }
    };

    const handleCompleteConsultation = (appointment) => {
        // Set selected appointment and show routing modal
        setSelectedAppointment(appointment);
        setShowRoutingModal(true);
    };

    const handleRoutingComplete = async (result) => {
        // Update appointment status to completed
        await handleUpdateAppointmentStatus(selectedAppointment.id, 'completed');
        
        // Show success message
        showAlert(result.message || '✅ Consultation completed and patient routed!', 'success');
        
        // If multi-step journey was created, show pathway slip
        if (result.journeySteps && result.journeySteps.length > 1) {
            setPathwayData({
                patient: selectedAppointment.patient,
                queueNumber: result.queueNumber,
                steps: result.journeySteps,
                diagnosis: diagnosis
            });
            setShowPathwaySlip(true);
        }
        
        // Close routing modal
        setShowRoutingModal(false);
        setSelectedAppointment(null);
        setDiagnosis('');
        
        // Refresh data
        fetchDoctorData();
    };


    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Doctor Dashboard</h2>
                    <p className="text-gray-600">Welcome back, Dr. {currentUser?.username}</p>
                </div>
                <div className="flex items-center gap-4">
                    <input
                        type="date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Calendar className="w-8 h-8 text-blue-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Today's Appointments</p>
                            <p className="text-2xl font-bold text-gray-900">{todayAppointments.length}</p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <User className="w-8 h-8 text-green-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">My Patients</p>
                            <p className="text-2xl font-bold text-gray-900">{myPatients.length}</p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <CheckCircle className="w-8 h-8 text-purple-500" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Completed Today</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {todayAppointments.filter(apt => apt.status === 'completed').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Today's Appointments */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            Today's Appointments ({selectedDate})
                        </h3>
                    </div>
                    <div className="p-6">
                        {todayAppointments.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>No appointments scheduled for today</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {todayAppointments.map((appointment) => (
                                    <div key={appointment.id} className="border rounded-lg p-4 hover:bg-gray-50">
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <Clock className="w-4 h-4 text-gray-500" />
                                                    <span className="font-medium text-gray-900">
                                                        {formatTime(appointment.appointmentTime)}
                                                    </span>
                                                    {appointment.priority && (
                                                        <span className={`text-xs font-medium ${getPriorityColor(appointment.priority)}`}>
                                                            {appointment.priority.toUpperCase()}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                <h4 className="font-semibold text-gray-900">
                                                    {appointment.patient?.name || 'Unknown Patient'}
                                                </h4>
                                                
                                                <p className="text-sm text-gray-600">
                                                    {appointment.appointmentType} • {appointment.department}
                                                </p>
                                                
                                                {appointment.patient?.symptoms && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Symptoms: {appointment.patient.symptoms}
                                                    </p>
                                                )}
                                                
                                                <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-2 ${getStatusColor(appointment.status)}`}>
                                                    {appointment.status}
                                                </span>
                                            </div>
                                            
                                            <div className="flex flex-col gap-2 ml-4">
                                                {appointment.status === 'scheduled' && (
                                                    <button
                                                        onClick={() => handleUpdateAppointmentStatus(appointment.id, 'confirmed')}
                                                        className="px-3 py-1 bg-green-100 text-green-800 rounded text-xs hover:bg-green-200"
                                                    >
                                                        Confirm
                                                    </button>
                                                )}
                                                
                                                {appointment.status === 'confirmed' && (
                                                    <button
                                                        onClick={() => handleUpdateAppointmentStatus(appointment.id, 'in-progress')}
                                                        className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200"
                                                    >
                                                        Start
                                                    </button>
                                                )}
                                                
                                                {appointment.status === 'in-progress' && (
                                                    <button
                                                        onClick={() => handleCompleteConsultation(appointment)}
                                                        className="px-3 py-2 bg-green-500 text-white rounded text-xs hover:bg-green-600 font-medium flex items-center gap-1"
                                                    >
                                                        <CheckCircle className="w-3 h-3" />
                                                        Complete & Route
                                                    </button>
                                                )}
                                                
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* My Patients */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <User className="w-5 h-5" />
                            My Recent Patients
                        </h3>
                    </div>
                    <div className="p-6">
                        {myPatients.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>No patients assigned yet</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {myPatients.map((patient) => (
                                    <div key={patient.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                                        <div className="flex-1">
                                            <h4 className="font-medium text-gray-900">{patient.name}</h4>
                                            <p className="text-sm text-gray-600">
                                                {patient.age ? `${patient.age} years` : 'Age not specified'} • {patient.phone}
                                            </p>
                                            {patient.symptoms && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {patient.symptoms.substring(0, 50)}...
                                                </p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                                                {patient.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Doctor Routing Modal */}
            {showRoutingModal && selectedAppointment && (
                <DoctorRoutingModal
                    patient={selectedAppointment.patient}
                    diagnosis={diagnosis}
                    onDiagnosisChange={setDiagnosis}
                    onComplete={handleRoutingComplete}
                    onCancel={() => {
                        setShowRoutingModal(false);
                        setSelectedAppointment(null);
                        setDiagnosis('');
                    }}
                />
            )}

            {/* Patient Pathway Slip */}
            {showPathwaySlip && pathwayData && (
                <PatientPathwaySlip
                    {...pathwayData}
                    onClose={() => {
                        setShowPathwaySlip(false);
                        setPathwayData(null);
                    }}
                />
            )}
        </div>
    );
};

export default DoctorDashboard;
